import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SharedLookbook, PrintLookbook } from '@/portal/components/Lookbook';
import { fetchShared } from '@/portal/api/client';
import { toProduct } from '@/portal/data/product';
import { useContent } from '@/services/siteData';
import { usePortalContact } from '@/portal/data/usePortalContact';
import type { SharedPortfolio, PortalProduct } from '@/portal/api/types';

export function SharedPage() {
  const { token = '' } = useParams();
  const content = useContent();
  const didier = usePortalContact();
  const phone = didier?.phone || content.contact.phone;
  const contact = {
    email: didier?.email || content.contact.email,
    phone,
    phoneHref: didier?.phone ? `tel:${didier.phone.replace(/\s+/g, '')}` : content.contact.phoneHref,
  };

  const [data, setData] = useState<SharedPortfolio | null>(null);
  const [status, setStatus] = useState<'loading' | 'ok' | 'missing'>('loading');
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    let alive = true;
    setStatus('loading');
    fetchShared(token)
      .then((d) => {
        if (!alive) return;
        setData(d);
        setStatus('ok');
      })
      .catch(() => {
        if (alive) setStatus('missing');
      });
    return () => {
      alive = false;
    };
  }, [token]);

  const products: PortalProduct[] = useMemo(() => (data ? data.products.map(toProduct) : []), [data]);

  if (status === 'loading') {
    return (
      <div className="portal">
        <div className="pl-login">
          <p style={{ color: 'var(--ink-faint)' }}>Loading presentation…</p>
        </div>
      </div>
    );
  }

  if (status === 'missing' || !data) {
    return (
      <div className="portal">
        <div className="pl-login">
          <div className="pl-login-card" style={{ textAlign: 'center', padding: 40 }}>
            <h1 style={{ marginBottom: 8 }}>Presentation unavailable</h1>
            <p className="sub">This shared link is no longer active. Please ask the sender for an up-to-date link.</p>
          </div>
        </div>
      </div>
    );
  }

  const meta = { title: data.title, client: data.client, event: data.event, note: data.note };

  return (
    <div className="portal">
      <SharedLookbook
        meta={meta}
        products={products}
        display={data.display}
        contact={contact}
        onPrint={() => setPrinting(true)}
      />
      {printing && (
        <PrintLookbook meta={meta} products={products} display={data.display} contact={contact} onClose={() => setPrinting(false)} />
      )}
    </div>
  );
}
